<template>
  <thead>
    <tr>
      <th
        v-for="col in columns"
        :key="col.key"
        class="ds-table__header-cell"
        :class="[{ 'ds-table__header-cell--sorted': sortColumn === col.key }, col.headerClass]"
        :style="col.width ? { width: col.width } : {}"
      >
        <slot :name="`header-${col.key}`" :column="col">
          <div class="ds-table__header-inner">
            <button
              type="button"
              class="ds-table__label"
              :class="{ 'ds-table__label--static': col.sortable === false }"
              @click="col.sortable !== false && $emit('toggle-sort', col.key)"
            >
              {{ col.label }}
            </button>
            <div class="ds-table__controls">
              <button
                v-if="col.sortable !== false"
                type="button"
                class="ds-table__sort"
                title="Sorteren"
                @click="$emit('toggle-sort', col.key)"
              >
                <span class="ds-table__arrow" :class="getSortClass(col.key, 'asc')">▲</span>
                <span class="ds-table__arrow" :class="getSortClass(col.key, 'desc')">▼</span>
              </button>

              <div v-if="col.filter" class="ds-table__filter">
                <button
                  type="button"
                  class="ds-table__filter-toggle"
                  :class="{ 'ds-table__filter-toggle--active': isFilterActive(col) }"
                  title="Filter"
                  @click.stop="toggleFilterPopover(col.key, $event)"
                >
                  ▼
                </button>

                <div
                  v-if="activeFilterColumn === col.key"
                  class="ds-popover"
                  :style="popoverStyle"
                  @click.stop
                >
                  <!-- Select -->
                  <template v-if="col.filter === 'select'">
                    <select
                      :value="filters[col.key]"
                      class="ds-popover__input"
                      autofocus
                      @change="$emit('set-filter', col.key, $event.target.value)"
                    >
                      <option value="">Geen selectie</option>
                      <option v-for="o in col.options" :key="o.value" :value="o.value">
                        {{ o.label }}
                      </option>
                    </select>
                    <button
                      v-if="filters[col.key]"
                      class="ds-popover__clear"
                      @click="$emit('set-filter', col.key, '')"
                    >
                      Wissen
                    </button>
                  </template>

                  <!-- Text -->
                  <template v-else-if="col.filter === 'text'">
                    <input
                      :value="filters[col.key]"
                      type="text"
                      class="ds-popover__input"
                      :placeholder="col.placeholder || ''"
                      autofocus
                      @input="$emit('set-filter', col.key, $event.target.value)"
                    />
                    <button
                      v-if="filters[col.key]"
                      class="ds-popover__clear"
                      @click="$emit('set-filter', col.key, '')"
                    >
                      Wissen
                    </button>
                  </template>

                  <!-- Date (single, lower bound) -->
                  <template v-else-if="col.filter === 'date'">
                    <label class="ds-popover__label">Vanaf</label>
                    <input
                      :value="filters[col.key]"
                      type="date"
                      class="ds-popover__input"
                      autofocus
                      @change="$emit('set-filter', col.key, $event.target.value)"
                    />
                    <button
                      v-if="filters[col.key]"
                      class="ds-popover__clear"
                      @click="$emit('set-filter', col.key, '')"
                    >
                      Wissen
                    </button>
                  </template>

                  <!-- Date range (rangeKey = lower bound, key = upper bound) -->
                  <template v-else-if="col.filter === 'date-range'">
                    <label class="ds-popover__label">Vanaf</label>
                    <input
                      :value="filters[col.rangeKey]"
                      type="date"
                      class="ds-popover__input"
                      autofocus
                      @change="$emit('set-filter', col.rangeKey, $event.target.value)"
                    />
                    <label class="ds-popover__label">Tot en met</label>
                    <input
                      :value="filters[col.key]"
                      type="date"
                      class="ds-popover__input"
                      @change="$emit('set-filter', col.key, $event.target.value)"
                    />
                    <button
                      v-if="isFilterActive(col)"
                      class="ds-popover__clear"
                      @click="$emit('clear-filters', [col.key, col.rangeKey])"
                    >
                      Wissen
                    </button>
                  </template>
                </div>
              </div>
            </div>
          </div>
        </slot>
      </th>

      <th
        v-if="hasActions"
        class="ds-table__header-cell ds-table__header-cell--actions"
        :style="actionsWidth ? { width: actionsWidth } : {}"
      ></th>
    </tr>
  </thead>
</template>

<script>
/**
 * Generic sortable + filterable table header.
 *
 * Presentation comes from the design system (styles/ds/_table.scss and
 * _popover.scss), which is emitted globally rather than under `.ds-root` — this
 * component also renders on pages that have not been migrated yet, and has to
 * look the same on all of them. It therefore carries no styles of its own.
 *
 * Column definition shape:
 * {
 *   key:         string,             // field name — used for sort and filter
 *   label:       string,             // display label
 *   width?:      string,             // e.g. '155px'
 *   sortable?:   boolean,            // default true
 *   filter?:     'select' | 'text' | 'date' | 'date-range' | null,
 *   options?:    { value, label }[], // required for filter: 'select'
 *   placeholder?: string,            // optional for filter: 'text'
 *   rangeKey?:   string,             // required for filter: 'date-range'
 *                                    //   the secondary filter key (lower bound)
 *   headerClass?: string,            // extra class on the <th> — a caller
 *                                    //   styles it through :deep()
 * }
 *
 * A caller may replace a column's entire header content (label, sort and
 * filter controls included) with the scoped slot `header-<key>` — used by
 * columns whose header is a custom layout, like the authorization matrix.
 *
 * Emits:
 *   toggle-sort(key)               — user clicked sort for a column
 *   set-filter(key, value)         — user changed a single filter value
 *   clear-filters([key, ...])      — user cleared a multi-key filter (date-range)
 */
export default {
  name: 'SortableTableHeader',
  props: {
    columns: { type: Array, required: true },
    filters: { type: Object, required: true },
    sortColumn: { type: String, default: null },
    sortDirection: { type: String, default: 'asc' },
    hasActions: { type: Boolean, default: false },
    actionsWidth: { type: String, default: '' },
  },
  emits: ['toggle-sort', 'set-filter', 'clear-filters'],
  data() {
    return {
      activeFilterColumn: null,
      popoverPosition: { top: 0, right: 0 },
    }
  },
  computed: {
    popoverStyle() {
      return {
        top: this.popoverPosition.top + 'px',
        right: this.popoverPosition.right + 'px',
      }
    },
  },
  mounted() {
    document.addEventListener('click', this.onDocumentClick)
  },
  beforeUnmount() {
    document.removeEventListener('click', this.onDocumentClick)
  },
  methods: {
    onDocumentClick() {
      this.activeFilterColumn = null
    },
    toggleFilterPopover(key, event) {
      if (this.activeFilterColumn === key) {
        this.activeFilterColumn = null
        return
      }
      const rect = event.currentTarget.getBoundingClientRect()
      this.popoverPosition = {
        top: rect.bottom + 4,
        right: window.innerWidth - rect.right,
      }
      this.activeFilterColumn = key
    },
    getSortClass(key, direction) {
      if (this.sortColumn !== key) return ''
      return this.sortDirection === direction ? 'ds-table__arrow--active' : ''
    },
    isFilterActive(col) {
      if (col.filter === 'date-range') {
        return !!(this.filters[col.key] || this.filters[col.rangeKey])
      }
      return !!this.filters[col.key]
    },
  },
}
</script>
